import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import { CreatorReviewTrait } from "../graphql/generated";
import styled from "../styles";
import withMargin, { WithMarginProp } from "../styles/withMargin";
import { Button } from "./CTA";
import { Flex } from "./Flex";
import { StarIcon } from "./icons/StarIcon";
import { Text } from "./Text";
import { ToggleButton } from "./ToggleButton";

interface StarReviewProps extends WithMarginProp {
  initialRating?: number;
  onSubmit?: (payload: {
    rating: number;
    traits: CreatorReviewTrait[];
  }) => void;
  options?: string[];
  size?: number; // pixel size for star icons
}

const StarsRow = styled.div<WithMarginProp>`
  display: flex;
  gap: ${(p) => p.theme.spacing.xs};
  align-items: center;
  ${withMargin}
  margin-left: -2px;
`;

const FollowUpWrap = styled.div`
  margin-top: ${(p) => p.theme.spacing.l};
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: ${(p) => p.theme.spacing.s};
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.s};
  align-items: center;
  justify-content: flex-start;
  margin-top: ${(p) => p.theme.spacing.xl};
`;

const StarButton = styled(motion.button)<{ $selected: boolean; $size: number }>`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  line-height: 0;
  cursor: pointer;

  svg {
    height: ${(p) => p.$size}px;
    width: ${(p) => p.$size}px;
    display: block;
  }

  & svg path {
    stroke: ${(p) =>
      p.$selected
        ? p.theme.color.reward
        : `${p.theme.color.typography.text}50`};
    fill: ${(p) =>
      p.$selected ? p.theme.color.reward : p.theme.color.card.background};
    transition: fill 220ms cubic-bezier(0.22, 1, 0.36, 1),
      stroke 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

const TRAIT_KEY_BY_LABEL: Record<string, CreatorReviewTrait> = {
  Communication: CreatorReviewTrait.Communication,
  "Content quality": CreatorReviewTrait.ContentQuality,
  Punctuality: CreatorReviewTrait.Punctuality,
  Creativity: CreatorReviewTrait.Creativity,
  "Followed brief": CreatorReviewTrait.FollowedBrief,
  "Something else": CreatorReviewTrait.Other,
};

export const StarReview: React.FC<StarReviewProps> = ({
  initialRating = 0,
  onSubmit,
  size = 24,
  margin,
}) => {
  const [rating, setRating] = useState<number>(initialRating);
  const [traits, setTraits] = useState<CreatorReviewTrait[]>([]);
  const [hovered, setHovered] = useState<number | null>(null);

  const followUpOptions = Object.keys(TRAIT_KEY_BY_LABEL);

  const handleRate = (value: number) => {
    setRating(value);
    setTraits([]);
  };

  const toggleReason = (reason: string) => {
    const traitEnum = TRAIT_KEY_BY_LABEL[reason];
    if (!traitEnum) return;

    setTraits((prev) => {
      const exists = prev.includes(traitEnum);
      const next = exists
        ? prev.filter((r) => r !== traitEnum)
        : [...prev, traitEnum];
      return next;
    });
  };

  const resetReview = () => {
    setRating(0);
    setTraits([]);
  };

  const isPositive = rating >= 4;
  const showFollowUp = rating > 0;

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit({
        rating,
        traits,
      });
    }
  };

  return (
    <div>
      <Flex direction="row" align="center">
        <StarsRow as={motion.div} layout margin={margin}>
          {Array.from({ length: 5 }).map((_, idx) => {
            const value = idx + 1;
            const selected =
              hovered !== null ? value <= hovered : rating >= value;
            return (
              <StarButton
                key={value}
                onClick={() => handleRate(value)}
                $selected={selected}
                $size={size}
                aria-label={`${value} star`}
                onMouseEnter={() => setHovered(value)}
                onMouseLeave={() => setHovered(null)}
                whileHover={{ scale: 1.08, rotate: -2 }}
                whileTap={{ scale: 0.92 }}
                animate={{ scale: selected ? 1.02 : 1, rotate: 0 }}
                transition={{
                  type: "spring",
                  stiffness: 500,
                  damping: 28,
                  mass: 0.4,
                }}
              >
                <StarIcon strokeWidth={1} />
              </StarButton>
            );
          })}
        </StarsRow>
      </Flex>

      <AnimatePresence initial={false}>
        {showFollowUp && (
          <FollowUpWrap
            as={motion.div}
            key="follow-up"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 250, damping: 28 }}
          >
            <Text
              as={motion.p}
              size="s"
              margin="0 0 s"
              initial={{ y: 6, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              {isPositive
                ? "What made it a success?"
                : rating < 3
                ? "What was the problem?"
                : "What could be improved?"}
            </Text>
            <OptionsGrid as={motion.div} initial={false}>
              {followUpOptions.map((reason, i) => (
                <motion.div
                  key={reason}
                  initial={{ y: 6, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{
                    duration: 0.2,
                    ease: [0.22, 1, 0.36, 1],
                    delay: i * 0.03,
                  }}
                >
                  <ToggleButton
                    title={reason}
                    size="s"
                    placement="card"
                    active={traits.includes(TRAIT_KEY_BY_LABEL[reason])}
                    onClick={() => toggleReason(reason)}
                  />
                </motion.div>
              ))}
            </OptionsGrid>
          </FollowUpWrap>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {showFollowUp && (
          <ActionsRow
            as={motion.div}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div whileTap={{ scale: 0.98 }}>
              <Button size="s" buttonType="secondary" onClick={resetReview}>
                Cancel
              </Button>
            </motion.div>
            <motion.div whileTap={{ scale: 0.98 }}>
              <Button size="s" onClick={handleSubmit}>
                Submit
              </Button>
            </motion.div>
          </ActionsRow>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StarReview;
